import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { authAPI } from '../../services/api';
import { useAuth } from '../../services/AuthContext';

const EditPromptModal = ({ isOpen, onClose, currentPrompt }) => {
  const { user, updateUser } = useAuth();
  const [prompt, setPrompt] = useState(
    currentPrompt || 
    user?.coachSettings?.config?.system_prompt ||
    "Focus on Socratic questioning and solution-focused brief therapy techniques. Avoid giving direct advice unless safety is a concern."
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update prompt whenever user settings or initial prompt change
  useEffect(() => {
    if (isOpen) {
      setPrompt(currentPrompt || user?.coachSettings?.config?.system_prompt || prompt);
    }
  }, [isOpen, currentPrompt, user?.coachSettings?.config?.system_prompt]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare the updated coach settings object
      const currentSettings = user?.coachSettings || {};
      const updatedSettings = {
        ...currentSettings,
        config: {
          ...(currentSettings.config || {}),
          system_prompt: prompt
        }
      };

      const response = await authAPI.updateMe({ coachSettings: updatedSettings });
      
      // Update local state in context
      updateUser({ coachSettings: response.data.coachSettings });
      
      onClose();
    } catch (err) {
      console.error('Failed to update prompt:', err);
      setError('Failed to save prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Base Knowledge">
      <div className="space-y-6">
        <p className="text-sm text-gray-600 leading-relaxed">
          Define the foundational philosophy and coaching style of your AI Ally. This prompt guides how the AI interprets situations and formulates responses for your clients.
        </p>

        {error && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center gap-2">
            <span className="material-symbols-outlined text-sm">error</span>
            {error}
          </div>
        )}

        <div>
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
            <span className="material-symbols-outlined text-sm">psychology</span>
            Core Philosophy
          </label>
          <textarea 
            rows="6"
            className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-4 shadow-inner resize-none transition-colors"
            placeholder="E.g., Always emphasize growth mindset and acknowledge small wins..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading}
          ></textarea>
          <p className="mt-2 text-[10px] text-gray-500 font-medium">
            Tip: Be specific about rules you want the AI to strictly follow (e.g., "Never suggest medication").
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button 
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-[var(--tangerine)] rounded-xl hover:bg-[#d97700] transition-colors shadow-md shadow-orange-500/20 disabled:opacity-70"
          >
            {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {loading ? 'Saving...' : 'Save Prompt'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditPromptModal;
